/**
 * Per-novel persistence of the testing/preview playthrough state
 * (flags, scales, completed endings) in localStorage, so that moving
 * between scenes via the "next scene" banner keeps the accumulated state.
 *
 * Flags/scales are stored as { [id]: value } maps so they survive
 * renames/additions to the novel's state definitions.
 */

const keyFor = (novelId) => `aiNovelTestState_${novelId}`;

export const loadTestState = (novelId) => {
  if (!novelId) return null;
  try {
    const raw = localStorage.getItem(keyFor(novelId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      flags: parsed.flags || {},
      scales: parsed.scales || {},
      completedEndingIds: parsed.completedEndingIds || [],
      userCharacterName: parsed.userCharacterName || '',
      userCharacterBiography: parsed.userCharacterBiography || ''
    };
  } catch {
    return null;
  }
};

export const saveTestState = (novelId, { flags, scales, completedEndingIds, userCharacterName, userCharacterBiography }) => {
  if (!novelId) return;
  try {
    const existing = loadTestState(novelId) || {};
    // Merge by id: removing a flag/scale from the novel leaves its stored value
    // untouched (ids are unique, so it never collides and returns if re-added).
    const flagMap = { ...(existing.flags || {}) };
    (flags || []).forEach(f => { flagMap[f.id] = f.value; });
    const scaleMap = { ...(existing.scales || {}) };
    (scales || []).forEach(s => { scaleMap[s.id] = s.value; });
    localStorage.setItem(keyFor(novelId), JSON.stringify({
      flags: flagMap,
      scales: scaleMap,
      completedEndingIds: completedEndingIds || [],
      userCharacterName: userCharacterName || '',
      userCharacterBiography: userCharacterBiography || ''
    }));
  } catch {
    // ignore quota / serialization errors
  }
};

export const clearTestState = (novelId) => {
  if (!novelId) return;
  try {
    localStorage.removeItem(keyFor(novelId));
  } catch {
    // ignore
  }
};

/**
 * Merge a novel's flag/scale definitions with a saved value map.
 * Returns runtime arrays of { id, name, value }, preferring the saved
 * value when present and falling back to the definition's initial_value.
 */
export const mergeSavedFlags = (definitions, savedMap) =>
  (definitions || []).map(f => ({
    id: f.id,
    name: f.name,
    value: savedMap && savedMap[f.id] !== undefined ? savedMap[f.id] : f.initial_value
  }));

export const mergeSavedScales = (definitions, savedMap) =>
  (definitions || []).map(s => {
    const saved = savedMap ? savedMap[s.id] : undefined;
    // Guard against corrupted saved values (NaN serializes to null in JSON).
    const value = saved !== undefined && saved !== null ? saved : (s.initial_value ?? 0);
    return { id: s.id, name: s.name, value };
  });
